import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {Privileges} from '../../../admin/privileges/privileges.constants';
import {AuthService} from '../../../../services/auth/auth.service';
import {ValidationService} from '../../../../services/validation/validation.service';
import {CustomerService} from '../../../../services/customer/customer.service';
import {EventService} from '../../../../services/event/event.service';
import {UserService} from '../../../../services/user/user.service';
import {CartService} from '../../../../services/cart/cart.service';
import {cloneDeep, find} from 'lodash-es';
import moment from "moment/moment";
import {
  EventLimited,
  EventStatus, RefundStatusEnum
} from '../../../../generated/sales-terminal-client';
import {ScrollLoad} from '../../../../components/scroll-load-component/ScrollLoad';
import {DatePickerConfig} from '../../../../components/datepicker/datepicker.component';
import {DATEPICKER_PRESETS_MAPPED} from '../../../../components/datepicker/datepicker.constants';
import {UtilService} from '../../../../services/util/util.service';
import {NavigationEnd, Router} from '@angular/router';
import {PaymentReversalsFilter} from '../../../../services/refund/refund.service';
import {Subscription} from 'rxjs';
import {LanguageService} from "../../../../services/language/language.service";
import {StorageService} from "../../../../services/storage/storage.service";
import {SELF_SERVICE_EXCLUDED_STATUSES, STATUS_DRAFT} from "../../event-search/event-search.constants";

@Component({
  selector: 'app-payment-reversal-filters',
  templateUrl: './payment-reversal-filters.component.html',
  styleUrls: ['./payment-reversal-filters.component.scss']
})
export class PaymentReversalFiltersComponent implements OnInit, OnDestroy {
  @HostListener('window:resize', ['$event']) onResize() {
    this.resizeHandler();
  };
  @ViewChild('searchForm') searchForm: ElementRef;

  private _filters: PaymentReversalsFilter;

  @Input() statusTabs: RefundStatusEnum[];

  public activeStatusTab: number = 0;
  @Input() statusTab: number;
  @Output() statusTabChange: EventEmitter<number> = new EventEmitter<number>();

  @Input() selectedIds: number[];

  @Input()
  public set filters(value: PaymentReversalsFilter) {
    this._filters = value;
    this.filtersChange.emit(this._filters);
  }
  public get filters(): PaymentReversalsFilter {
    return this._filters;
  }
  @Output()
  public filtersChange: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  public search: EventEmitter<any> = new EventEmitter<any>();

  @Input()
  public isLoading: boolean;

  @Input()
  public showMobileFilters: boolean;
  @Output()
  public showMobileFiltersChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  public hasResults: boolean;

  @Input()
  public noSearches: boolean;
  @Output()
  public noSearchesChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  public refundClicked: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  public refundAllClicked: EventEmitter<any> = new EventEmitter<any>();

  @Input()
  public eventId: number;

  public updatingData: boolean;
  public eventStatusId: number = null;
  public eventStatuses: EventStatus[] = [];
  public eventScrollLoad: ScrollLoad<EventLimited> = this.eventService.getScrollLoad();
  private _defaultFilters: PaymentReversalsFilter = {
    reversalStartDate: moment().add(-1, 'day').format(),
    reversalEndDate: moment().add(-1, 'day').format(),
    refundStatus: null,
    eventId: null,
    orderConfirmation: null
  };
  private _defaultDatePickerConfig: DatePickerConfig = {
    range: true,
    autoBindDates: false,
    allowDateClear: false,
    maxDate: moment().format(),
    noHint: true,
    errorMessage: 'general.api_search_range_month',
    dateRanges: [DATEPICKER_PRESETS_MAPPED.Yesterday, DATEPICKER_PRESETS_MAPPED.Today, DATEPICKER_PRESETS_MAPPED.Current_week],
    defaultPreset: DATEPICKER_PRESETS_MAPPED.Yesterday
  };
  public dateRangeConfig: DatePickerConfig = null;
  private _subscriptions: Subscription[] = [];

  get fixedSnackHeight(): string {
    return 'margin-top: ' + this.utilService.fixedSnackHeight + 'px';
  }

  constructor(public authService: AuthService,
              public validationService: ValidationService,
              public customerService: CustomerService,
              public eventService: EventService,
              public userService: UserService,
              public utilService: UtilService,
              public cartService: CartService,
              private componentRef: ElementRef,
              private changeRef: ChangeDetectorRef,
              private storageService: StorageService,
              private router: Router) {
  }

  public ngOnInit(): void {
    this.setupDatePicker();
    this.filters = cloneDeep(this._defaultFilters);
    this._subscriptions.push(this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.filters.eventId = null;
        this.eventScrollLoad.reset();
        setTimeout(() => {
          this.setupDatePicker();
        }, 0);
      }
    }));
    this._subscriptions.push(this.storageService.changes.subscribe(({ key }) => {
      if (key === LanguageService.STORAGE_LANGUAGE_SELECTED) {
        this.languageLoader();
      }
    }));
    this._subscriptions.push(this.utilService.contentScrolled.subscribe((value) => {
      if (value) {
        this.languageLoader();
      }
    }));
    this._subscriptions.push(this.utilService.translationsLoadedChange.subscribe((value) => {
      if (value) {
        this.languageLoader();
      }
    }));
    this._subscriptions.push(this.utilService.snackDismissed.subscribe(() => {
      setTimeout(() => {
        this.resizeHandler();
      }, 100);
    }));
    this.languageLoader();
    this.updateData();
  }
  public ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
    this._subscriptions = [];
  }

  languageLoader(): void {
    setTimeout(() => {
      this.resizeHandler();
    }, 100);
  }

  ngAfterViewInit(): void {
    this.resizeHandler();
  }

  public get buttonsEnabled(): boolean {
    return [RefundStatusEnum.NotStarted, RefundStatusEnum.Failed].includes(this.statusTabs[this.activeStatusTab]) && !this.isLoading && this.hasResults;
  }

  changeTab(): void {
    this.statusTab = this.activeStatusTab;
    this.statusTabChange.emit(this.statusTab);
    if (!this.showMobileFilters) {
      this.applySearch(1);
    }
    setTimeout(() => {
      this.resizeHandler();
    }, 100);
  }

  private setupDatePicker(): void {
    this.dateRangeConfig = {
      ...this._defaultDatePickerConfig
    };
  }

  public updateData(): void {
    this.eventService.findAllEventStatuses().subscribe(results => {
      this.eventStatuses = results.filter((item: EventStatus) => !SELF_SERVICE_EXCLUDED_STATUSES.includes(item.id));
    });
  }

  public toggleFilters() {
    this.showMobileFilters = !this.showMobileFilters;
    this.showMobileFiltersChange.emit(this.showMobileFilters);
  }

  public clearFilters() {
    const filters = cloneDeep(this._defaultFilters);
    Object.keys(filters).forEach((key) => {
      (filters as any)[key] = null;
    });
    this.eventScrollLoad.reset();
    this.eventStatusId = null;
    this.filters = filters;
    this.noSearches = true;
    this.noSearchesChange.emit(this.noSearches);
  }

  displayFilterSelectedOption(id: number, list: Array<any>): string {
    return find(list, {id: id})?.name;
  }

  public applyMobileDateChange(): void {
    if (this.filters.reversalStartDate || this.filters.reversalEndDate) {
      this.applySearch();
    }
  }

  public applySearch(page?: number): void {
    if (!this.filters.eventId) {
      this.eventScrollLoad.reset();
    }
    this.search.emit(page);
  }

  resizeHandler(): void {
    this.changeRef.markForCheck();
  }

  protected readonly Privileges = Privileges;
}
